import CustomTooltip from '../../../shared/CustomTooltip';
import { Box, Checkbox, Stack, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import ExperiencePoliciesForm from './ExperiencePoliciesForm';
import { useFormContext, useWatch } from 'react-hook-form';
import { TooltipCancellationContent } from '../../Policies/PolicyTooltipContent';
import { bookingPoliciesContent } from '../../Policies/PolicyForm';
import { useFormState } from '../Form/useFormState';
import { z } from 'zod';
import { PoliciesSchema } from '../Form/Policies/schema';

interface Props {
    experienceType?: string;
}

type FormValues = z.infer<typeof PoliciesSchema>;

const ExperiencePolicies = ({ experienceType }: Props) => {
    const { experience } = useFormState();
    const theme = useTheme();
    const [hasPolicies, setHasPolicies] = useState(!!experience.policy);

    const { control, setValue } = useFormContext<FormValues>();
    const policy = useWatch({ control, name: 'policy' });

    const handleCheck = () => {
        setHasPolicies(!hasPolicies);

        if (!hasPolicies) {
            setValue(
                'policy',
                {
                    requiresConsent: false,
                    policyId: -1,
                },
                { shouldDirty: true },
            );
        } else {
            setValue('policy', null, { shouldDirty: true });
        }
    };

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2'>Policies (optional)</Typography>
                    <Typography sx={{ color: 'text.secondary' }} gutterBottom>
                        Apply a policy to your experience to enforce refund
                        policies.
                    </Typography>
                    <Typography
                        variant='body3'
                        sx={{ color: 'text.secondary' }}
                    >
                        Note: If no policy is applied we will apply the{' '}
                        <CustomTooltip
                            title={
                                <TooltipCancellationContent
                                    content={bookingPoliciesContent}
                                />
                            }
                            placement='right'
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        maxHeight: 400,
                                        overflowY: 'auto',
                                    },
                                },
                            }}
                        >
                            <span
                                style={{
                                    cursor: 'pointer',
                                    color: theme.palette.textAccent.main,
                                    textDecoration: 'underline',
                                }}
                            >
                                'Fair Cancellation Policy'
                            </span>
                        </CustomTooltip>
                    </Typography>
                </div>
                <Box display='flex' gap={1}>
                    <Checkbox
                        id='has-policies__input'
                        checked={hasPolicies}
                        onChange={handleCheck}
                        sx={{ p: 0 }}
                    />
                    <Typography component='label' htmlFor='has-policies__input'>
                        This Experience has a policy
                    </Typography>
                </Box>
                {hasPolicies && (
                    <ExperiencePoliciesForm
                        policy={policy}
                        experienceType={experience.type}
                    />
                )}
            </Stack>
        </fieldset>
    );
};

export default ExperiencePolicies;
